import React from 'react';

import { IndustryTechStackItem, TechItem } from '../../data/industryData';
import '../../styles/ScrollIndustryTechStack.css';
import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);


interface ScrollTechStackProps {
  items: IndustryTechStackItem[];
}

// Function to map icon string to image path
const getIconForTech = (iconName?: string) => {
  if (!iconName) return require('../../assets/Icons/default.png'); // Default icon
  
  // Map of icon identifiers to their corresponding image paths
  const iconMap: Record<string, string> = {
    // Cloud providers
    'aws': require('../../assets/Icons/aws.png'),
    'gcp': require('../../assets/Icons/gcp.png'),
    'azure': require('../../assets/Icons/azure.png'),
    'argocd': require('../../assets/Icons/argocd.png'),

    
    // Infrastructure
    'terraform': require('../../assets/Icons/terraform.png'),
    'terragrunt': require('../../assets/Icons/terragrunt.png'),
    'pulumi': require('../../assets/Icons/pulumi.png'),
    'atlantis': require('../../assets/Icons/atlantis.png'),
    'opentofu': require('../../assets/Icons/opentofu.png'),
    
    // Network
    'connect': require('../../assets/Icons/connect.png'),
    'link': require('../../assets/Icons/link.png'),
    'network': require('../../assets/Icons/network.png'),
    
    // Security
    'vault': require('../../assets/Icons/vault.png'),
    'opa': require('../../assets/Icons/opa.png'),
    'kyverno': require('../../assets/Icons/kyverno.png'),
    
    // // Monitoring
    'prometheus': require('../../assets/Icons/prometheus.png'),
    'grafana': require('../../assets/Icons/grafana.png'),
    'elk': require('../../assets/Icons/elk.png'),
    'opentelemetry': require('../../assets/Icons/opentelemetry.png'),
    
    // CI/CD & GitOps
    'github': require('../../assets/Icons/github.png'),
    'gitlab': require('../../assets/Icons/gitlab.png'),
    'argo': require('../../assets/Icons/argocd.png'),
    'helm': require('../../assets/Icons/helm.png'),
    'kustomize': require('../../assets/Icons/kustomize.png'),
    'jenkins': require('../../assets/Icons/jenkins.png'),
    'fluxcd': require('../../assets/Icons/fluxcd.png'),
    'hashicorp': require('../../assets/Icons/hashicorpvault.png'),
    'checkov': require('../../assets/Icons/checkov.png'),

    
    // Machine Learning
    'kubeflow': require('../../assets/Icons/kubeflow.png'),
    'mlflow': require('../../assets/Icons/mlflow.png'),
    'airflow': require('../../assets/Icons/airflow.png'),
    'seldon': require('../../assets/Icons/seldon.png'),
    'infracost': require('../../assets/Icons/infracost.png'),
    'kubecost': require('../../assets/Icons/kubecost.png'),
    'anthos':require('../../assets/Icons/anthos.png'),
    'openshift':require('../../assets/Icons/openshift.png'),
    'kubernetes':require('../../assets/Icons/kubernets.png'),
    'cloudformation':require('../../assets/Icons/cloudformation.png'),
    'externalsecrets':require('../../assets/Icons/externalsecret.png'),
    'kserve':require('../../assets/Icons/kserve.png'),
    'knative':require('../../assets/Icons/knative.png'),
    'tensorboard':require('../../assets/Icons/tensorboard.png'),
    'siem':require('../../assets/Icons/SIEM.png'),
    'iam':require('../../assets/Icons/iam.png'),
    'encryption':require('../../assets/Icons/encryption.png'),
    'etl':require('../../assets/Icons/etlPipelines.png'),
    'redis':require('../../assets/Icons/redis.png'),
    'cdn':require('../../assets/Icons/cdn.png'),
    'cart':require('../../assets/Icons/cartandcheckout.png'),
    'fraud':require('../../assets/Icons/frauddetection.png'),
    'identity':require('../../assets/Icons/frauddetection.png'),
    'data':require('../../assets/Icons/dataPrivacy.png'),
    'product':require('../../assets/Icons/catlog.png'),
    'pci':require('../../assets/Icons/security.png'),
    'a/b':require('../../assets/Icons/security.png'),
    'ehr':require('../../assets/Icons/security.png'),
    'anonymization':require('../../assets/Icons/frauddetection.png'),
    'order':require('../../assets/Icons/orderProcessing.png'),
    'hl7/fhir':require('../../assets/Icons/orderProcessing.png'),
    'customer':require('../../assets/Icons/customerjourney.png'),
    'conversion':require('../../assets/Icons/conversion.png'),
    'inventory':require('../../assets/Icons/inventory.png'),
    'payments':require('../../assets/Icons/payment.png'),
    'genomic':require('../../assets/Icons/inventory.png'),
    'device':require('../../assets/Icons/connectivity.png'),
    'telemedicine':require('../../assets/Icons/Telemedicine.png'),
    'healthcare':require('../../assets/Icons/Telemedicine.png'),
    'confidential':require('../../assets/Icons/security.png'),
    'clinical':require('../../assets/Icons/inventory.png'),

    // Default
    'default': require('../../assets/Icons/default.png')
  };
  
  return iconMap[iconName.toLowerCase()] || iconMap.default;
};

const ScrollTechStack: React.FC<ScrollTechStackProps> = ({ items }) => {
  const categoryRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    categoryRefs.current.forEach((el) => {
      if (!el || !el.parentElement) return;
  
      const parent = el.parentElement;
      const height = el.offsetHeight;
      const parentHeight = parent.offsetHeight;
  
      gsap.fromTo(
        el,
        { y: 0 },
        {
          y: parentHeight - height,
          ease: 'none',
          scrollTrigger: {
            trigger: parent,
            start: 'top top',      
            end: () => `+=${parentHeight - height}`, 
            scrub: true,
          },
        }
      );
    });
  }, []);

  return (
    <section className="scroll-tech-stack-section">
      <div className="scroll-tech-stack-container">
        {items.map((item, index) => (
          <div className="scroll-tech-stack-category-block" key={index}>
            <div
              className="scroll-category-name"
              ref={el => void (categoryRefs.current[index] = el)}
              >
              <h3 className="scroll-category-title">{item.title}</h3>
            </div>

            <div className="scroll-tech-list">
              {item.technologies.map((tech, i) => {
                const isObject = typeof tech !== 'string';
                const techName = isObject ? (tech as TechItem).name : tech;
                const techIcon = isObject ? (tech as TechItem).icon : undefined;

                const iconPath = getIconForTech(
                  techIcon || techName.toLowerCase().split(' ')[0]
                );

                return (
                  <div className="scroll-tech-item" key={i}>
                    <span className="scroll-industry-tech-icon">
                      <img
                        src={iconPath}
                        alt={techName}
                        className="scroll-industry-tech-icon-image"
                      />
                    </span>
                    <span className="scroll-tech-name">{techName}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};


export default ScrollTechStack;